// Auth.jsx (UI-only: LoginPage + AuthGate)
// Note: Authentication state and actions are provided by src/context/AuthContext

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CircularProgress } from '@mui/material';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { LoadingSpinner, FormInput, PasswordInput } from './Common.jsx';
import PlatformLayout from './PlatformLayout.jsx';
import { useAuth } from '../context/useAuth';
import PropTypes from 'prop-types';
import { ROLES } from '../utils/roles';
import { apiGet, saveTokens } from '../api';
import BRAND from '../config/branding';

// Simple Email Regex for client-side validation
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

import { AlertCircle, CheckCircle2 } from 'lucide-react'; // NEW IMPORT

// --- Page Imports ---
import AdminDashboardPage from '../pages/AdminDashboardPage';
import PartnersPage from '../pages/PartnersPage';
import PartnerDetailPage from '../pages/PartnerDetailPage';
import TenantsPage from '../pages/TenantsPage';
import TenantDetailPage from '../pages/TenantDetailPage';
import UsersPage from '../pages/UsersPage';
import RolesPermissionsPage from '../pages/RolesPermissionsPage';
import BusinessAppsPage from '../pages/BusinessAppsPage';
import DataModelsPage from '../pages/DataModelsPage.tsx'; // NEW: Canvas-based designer

import DatasetsPage from '../pages/DatasetsPage';
import DashboardViewPage from '../pages/dashboard/DashboardViewPage';
import DashboardsPage from '../pages/dashboard/DashboardsPage';
import DashboardsLandingPage from '../pages/dashboard/DashboardsLandingPage';
import SettingsPage from '../pages/SettingsPage';
import AlertRulesPage from '../pages/AlertRulesPage';
import MLAppRunnerPage from '../pages/MLAppRunnerPage';
import CreditsPage from '../pages/CreditsPage';
import ProfilePage from '../pages/ProfilePage';
import DqProfilerPage from '../pages/DqProfilerPage';

const LoginPage = ({ onLogin, onRequestMagicLink, onMagicLogin, loginWithToken }) => {
    const { verifyMfa, resendMfa } = useAuth(); // Need verifyMfa and resendMfa from context <== CHANGED
    const location = useLocation();

    // Check for URL Token immediately
    const initialToken = new URLSearchParams(location.search).get('token');

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    // Initialize loading/message if token is present
    const [message, setMessage] = useState(initialToken ? "Verifying magic link..." : '');
    const [isLoading, setIsLoading] = useState(!!initialToken);

    // Dedicated state to hide form during URL token verification
    const [isVerifyingUrlToken, setIsVerifyingUrlToken] = useState(!!initialToken);

    const [magicLinkSent, setMagicLinkSent] = useState(false);

    // Auth Method Toggle: 'password' | 'magic'
    const [authMethod, setAuthMethod] = useState('password');

    // --- MFA State ---
    const [showMfaInput, setShowMfaInput] = useState(false);
    const [mfaSessionToken, setMfaSessionToken] = useState(null);
    const [otpCode, setOtpCode] = useState('');

    // --- Password Expired Lockout State ---
    const [passwordExpired, setPasswordExpired] = useState(false);
    const [resetLinkSent, setResetLinkSent] = useState(false);

    // --- MFA RESEND ---
    const [resendTimer, setResendTimer] = useState(30);
    const [isResending, setIsResending] = useState(false);

    useEffect(() => {
        let interval;
        if (showMfaInput && resendTimer > 0) {
            interval = setInterval(() => setResendTimer(prev => prev - 1), 1000);
        }
        return () => clearInterval(interval);
    }, [showMfaInput, resendTimer]);

    const handleResendCode = async () => {
        setIsResending(true);
        try {
            await resendMfa(mfaSessionToken);
            setMessage("Code resent successfully!");
            setResendTimer(30);
        } catch (err) {
            setMessage(err.message || "Failed to resend code.");
        } finally {
            setIsResending(false);
        }
    };

    // Handle URL Magic Token

    const processedTokenRef = React.useRef(null);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const token = params.get('token');

        // Prevent double-processing (React StrictMode fix)
        if (token && processedTokenRef.current !== token) {
            processedTokenRef.current = token;
            handleMagicLogin(token);

            // Clean URL history to remove token
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, [location]);

    const handleMagicLogin = async (token) => {
        setIsLoading(true);
        setIsVerifyingUrlToken(true);
        setMessage("Verifying magic link...");
        try {
            await onMagicLogin(token);
        } catch (error) {
            if (error.isSsaPending) {
                // Show the same SSA waiting screen as password login
                setSsaRequestId(error.requestId);
                setSsaStatus('pending');
                setSsaTimeout(error.timeout || 60);
                setIsVerifyingUrlToken(false);
            } else {
                setMessage("Invalid or expired magic link.");
                setIsVerifyingUrlToken(false); // Reveal form on error
            }
            setIsLoading(false);
        }
    };

    const [ssaRequestId, setSsaRequestId] = useState(null);
    const [ssaStatus, setSsaStatus] = useState('pending'); // pending | approved | rejected | expired
    const [ssaTimeout, setSsaTimeout] = useState(30);

    const handlePasswordLogin = async (e) => {
        e.preventDefault();
        
        // Grab values directly from the DOM to bypass React state desyncs caused by Chrome autofill
        const form = e.target.closest('form') || e.target;
        const actualEmail = form?.email?.value || email;
        const actualPassword = form?.password?.value || password;

        setIsLoading(true);
        setMessage('');
        try {
            await onLogin(actualEmail, actualPassword);
        } catch (error) {
            // Check for SSA Pending Requirement
            if (error.isSsaPending) {
                // Stamp sessionStorage so SessionRevokedModal knows this window is the
                // REQUESTER for this request_id and should NOT show the Allow/Block modal.
                sessionStorage.setItem('ssa_pending_request', error.requestId);
                setSsaRequestId(error.requestId);
                setSsaStatus('pending');
                setSsaTimeout(error.timeout || 30);
                setMessage('');
            }
            // Check for MFA Requirement
            else if (error.isMfaRequired) {
                setMfaSessionToken(error.mfaSessionToken);
                setShowMfaInput(true);
                setMessage(`Enter the code sent to your ${error.mfaMethod || 'email'}`);
            }
            // Check for Password Expiry
            else if (error.isPasswordExpired) {
                setPasswordExpired(true);
                setMessage('');
            } else {
                setMessage(error.message || "Login failed.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    // ── SSA: WebSocket-first status listener (HTTP polling as fallback) ───────────
    const ssaWsRef = useRef(null);
    const ssaFallbackRef = useRef(null);

    // Stable handler so it can be referenced in both WS and poll paths
    const handleSsaResult = useCallback((res) => {
        if (res.status === 'approved') {
            setSsaStatus('approved');
            sessionStorage.removeItem('ssa_pending_request');
            saveTokens(res.access_token, res.refresh_token);
            if (res.session_id) sessionStorage.setItem('auth_sid', res.session_id);
            if (loginWithToken) loginWithToken(res.access_token, res.session_id, false, res.refresh_token);
            // Delay clearing the ssa request ID to prevent the AuthGate from briefly showing the login page 
            // before the loginWithToken redirect completes.
            setTimeout(() => setSsaRequestId(null), 1000);
        } else if (res.status === 'rejected') {
            setSsaStatus('rejected');
            sessionStorage.removeItem('ssa_pending_request');
            setSsaRequestId(null);
            setMessage('Login rejected by the existing session.');
        } else if (res.status === 'not_found') {
            // Record gone before WS connected — extremely rare edge case
            setSsaStatus('expired');
            sessionStorage.removeItem('ssa_pending_request');
            setSsaRequestId(null);
            setMessage('Session request not found. Please try logging in again.');
        }
        // Note: 'expired' is no longer sent by the backend in normal flow.
        // When the timer runs out with no Tab 1 action, the backend auto-approves
        // and sends 'approved' with tokens — Tab 2 is automatically logged in.
    }, [loginWithToken]);


    useEffect(() => {
        if (!ssaRequestId || ssaStatus !== 'pending') return;

        // Derive base URLs
        const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
        const WS_BASE  = API_BASE.replace(/^http/, 'ws');
        const WS_PATH  = WS_BASE.includes('/api/v1') ? '/auth/sessions/ssa-status-ws' : '/api/v1/auth/sessions/ssa-status-ws';
        const wsUrl    = `${WS_BASE}${WS_PATH}/${ssaRequestId}`;

        let ws = null;
        let fallbackActive = false;
        let cancelled = false;

        // ── Countdown timer (purely visual) ───────────────────────────────────────
        const countdownId = setInterval(() => {
            setSsaTimeout(prev => (prev > 0 ? prev - 1 : 0));
        }, 1000);

        // ── HTTP fallback (used if WS fails or is blocked by proxy) ───────────
        const startHttpFallback = () => {
            if (fallbackActive || cancelled) return;
            fallbackActive = true;
            console.warn('[SSA] WS unavailable — falling back to HTTP polling (5s interval)');
            ssaFallbackRef.current = setInterval(async () => {
                if (cancelled) return;
                try {
                    const res = await apiGet(`/auth/sessions/ssa-status/${ssaRequestId}`);
                    if (res.status !== 'pending') handleSsaResult(res);
                } catch (err) {
                    console.error('[SSA] HTTP fallback poll error:', err);
                }
            }, 5000);
        };

        // ── Primary: WebSocket ─────────────────────────────────────────────
        try {
            ws = new WebSocket(wsUrl);
            ssaWsRef.current = ws;

            ws.onmessage = (event) => {
                if (cancelled) return;
                try {
                    const data = JSON.parse(event.data);
                    console.log('[SSA] WS status received:', data.status);
                    handleSsaResult(data);
                } catch (e) {
                    console.error('[SSA] WS message parse error:', e);
                }
            };

            ws.onerror = () => {
                if (!cancelled) startHttpFallback();
            };

            // WS closed by server after sending result — normal behaviour
            ws.onclose = (e) => {
                if (!cancelled && !fallbackActive && e.code !== 1000) {
                    // Abnormal close before we got a result — activate fallback
                    startHttpFallback();
                }
            };

            // If WS doesn't connect within 3s, activate fallback (proxy issues)
            const wsConnectTimeout = setTimeout(() => {
                if (!cancelled && ws.readyState !== WebSocket.OPEN) {
                    startHttpFallback();
                }
            }, 3000);

            ws.onopen = () => clearTimeout(wsConnectTimeout);

        } catch (err) {
            // WebSocket constructor can throw in some environments
            startHttpFallback();
        }

        return () => {
            cancelled = true;
            clearInterval(countdownId);
            if (ssaFallbackRef.current) clearInterval(ssaFallbackRef.current);
            if (ws && ws.readyState < WebSocket.CLOSING) ws.close();
        };
    }, [ssaRequestId, ssaStatus, handleSsaResult]);

    // --- RENDER SSA PENDING ---
    if (ssaRequestId && ssaStatus === 'pending') {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center relative">
                <div className="max-w-md w-full bg-white shadow-md rounded-lg p-8 text-center">
                    <div className="bg-primary/10 text-primary p-3 rounded-full inline-flex mb-4">
                        <CircularProgress size={32} color="inherit" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900">Awaiting Approval</h1>
                    <p className="mt-4 text-gray-600">
                        You are already logged in on another device.
                    </p>
                    <p className="mt-2 text-sm text-gray-500 font-medium">
                        Waiting for the existing session to approve your login attempt...
                    </p>
                    {ssaTimeout > 0 && (
                        <div className="mt-5 space-y-3">
                            <p className="text-xs text-gray-400 uppercase tracking-widest">
                                Request expires in <span className="font-mono font-bold text-primary">{ssaTimeout}s</span>
                            </p>
                            <div className="bg-amber-50 border border-amber-100 rounded-md p-3 mx-4">
                                <p className="text-xs text-amber-700 italic font-medium leading-relaxed">
                                    Note: If the existing session is inactive, your login will be <strong className="font-semibold text-amber-800">auto-approved</strong> when the timer expires.
                                </p>
                            </div>
                        </div>
                    )}
                    <div className="mt-8">
                        <button
                            onClick={() => {
                                setSsaRequestId(null);
                                setSsaStatus('pending');
                            }}
                            className="text-sm text-primary hover:text-primary-dark font-medium hover:underline"
                        >
                            Cancel and go back
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // --- RENDER PASSWORD EXPIRED ---
    if (passwordExpired) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center relative">
                <div className="max-w-md w-full bg-white shadow-md rounded-lg p-8 text-center">
                    {!resetLinkSent ? (
                        <>
                            <div className="bg-red-100 text-red-600 p-3 rounded-full inline-flex mb-4">
                                <AlertCircle size={32} strokeWidth={2} />
                            </div>
                            <h1 className="text-2xl font-bold text-gray-900 mb-4">Password Expired</h1>
                            <p className="text-gray-600 mb-6 text-sm">
                                Your password has expired in accordance with your organization's security policy.
                                Please request a secure reset link to regain access.
                            </p>
                            <button
                                onClick={async () => {
                                    setIsLoading(true);
                                    try {
                                        await apiFetch("/auth/forgot-password", null, {
                                            method: "POST",
                                            body: JSON.stringify({ email }),
                                        });
                                        setResetLinkSent(true);
                                    } catch (err) {
                                        setMessage("Failed to send reset link.");
                                    } finally {
                                        setIsLoading(false);
                                    }
                                }}
                                disabled={isLoading}
                                className="w-full py-3 px-4 bg-primary hover:bg-primary-dark text-white font-bold rounded-lg shadow-md transition-colors flex justify-center items-center"
                            >
                                {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Send Reset Link'}
                            </button>
                        </>
                    ) : (
                        <>
                            <div className="bg-green-100 text-green-600 p-3 rounded-full inline-flex mb-4">
                                <CheckCircle2 size={32} strokeWidth={2} />
                            </div>
                            <h1 className="text-2xl font-bold text-gray-900 mb-4">Check Your Email</h1>
                            <p className="text-gray-600 mb-6 text-sm">
                                We've sent a secure password reset link to <strong>{email}</strong>.
                            </p>
                        </>
                    )}

                    <button
                        onClick={() => {
                            setPasswordExpired(false);
                            setResetLinkSent(false);
                        }}
                        className="mt-6 text-sm text-gray-500 hover:text-gray-700 hover:underline inline-block w-full text-center"
                    >
                        Back to Login
                    </button>
                    {message && (
                        <p className="mt-4 text-center text-sm font-medium text-red-600 bg-red-50 p-2 rounded">
                            {message}
                        </p>
                    )}
                </div>
            </div>
        );
    }

    const handleMfaVerify = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage('');
        try {
            await verifyMfa(mfaSessionToken, otpCode);
        } catch (error) {
            setMessage(error.message || "Invalid OTP code.");
        } finally {
            setIsLoading(false);
        }
    }

    const handleMagicLinkRequest = async (e) => {
        e.preventDefault(); // Prevent form submission if button is inside form

        if (!email) {
            setMessage("Please enter your email address.");
            return;
        }
        if (!EMAIL_REGEX.test(email)) {
            setMessage("Please enter a valid email address.");
            return;
        }

        setIsLoading(true);
        setMessage('');
        try {
            await onRequestMagicLink(email);
            setMagicLinkSent(true);
        } catch (error) {
            console.error("[AuthContext] Magic link request failed:", error);
            setMessage(error.message || "Failed to send magic link.");
        } finally {
            setIsLoading(false);
        }
    };

    // --- RENDER URL TOKEN VERIFICATION ---
    if (isVerifyingUrlToken) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center relative">
                <div className="text-center">
                    <LoadingSpinner size={48} />
                    <p className="mt-4 text-gray-500 font-medium animate-pulse">{message}</p>
                </div>
            </div>
        );
    }

    // --- RENDER MFA INPUT ---
    if (showMfaInput) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center relative">
                <div className="max-w-md w-full bg-white shadow-md rounded-lg p-8">
                    <div className="text-center mb-8">
                        <div className="bg-primary/10 text-primary p-3 rounded-full inline-flex mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900">Verify Identity</h1>
                        <p className="mt-2 text-sm text-gray-500">
                            {message || "Enter the One-Time Password sent to you."}
                        </p>
                    </div>

                    <form onSubmit={handleMfaVerify} className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium mb-1.5 text-gray-700 text-center">One-Time Password</label>
                            <input
                                value={otpCode}
                                onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                type="text"
                                placeholder="123456"
                                className="w-full px-4 py-3 text-center text-2xl tracking-widest border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary bg-white transition-all shadow-sm font-mono"
                                required
                                autoFocus
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={isLoading || otpCode.length < 6}
                            className="w-full h-11 px-4 py-3 text-sm font-bold text-white bg-primary hover:bg-primary-dark disabled:opacity-70 rounded-lg shadow-lg hover:shadow-primary/30 transition-all flex items-center justify-center"
                        >
                            {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Verify Code'}
                        </button>

                        {/* RESEND LINK */}
                        <div className="text-center">
                            <button
                                type="button"
                                disabled={resendTimer > 0 || isResending}
                                onClick={handleResendCode}
                                className="text-sm text-primary hover:text-primary-dark disabled:text-gray-400 disabled:cursor-not-allowed font-medium hover:underline transition-colors"
                            >
                                {isResending ? 'Sending...' : resendTimer > 0 ? `Resend code in ${resendTimer}s` : 'Resend Code'}
                            </button>
                        </div>

                        <button
                            type="button"
                            onClick={() => setShowMfaInput(false)}
                            className="w-full text-sm text-gray-500 hover:text-gray-700 hover:underline"
                        >
                            Back to Login
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col lg:flex-row relative" style={{ fontFamily: "'Inter', sans-serif" }}>

            {/* Magic Link Success Popup */}
            {magicLinkSent && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center relative">

                        <button
                            onClick={() => setMagicLinkSent(false)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                            aria-label="Close dialog"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        <div className="mx-auto w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-4">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                        </div>

                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                            Check your email
                        </h3>

                        <p className="text-gray-600 mb-2">
                            We've sent a secure login link to your registered email address.
                        </p>

                        <p className="text-sm text-gray-500 mb-6">
                            The link will expire in 15 minutes.
                        </p>

                        <button
                            onClick={() => setMagicLinkSent(false)}
                            className="w-full py-2.5 px-4 bg-primary hover:bg-primary-dark text-white font-semibold rounded-lg shadow-md transition-colors"
                        >
                            OK, got it
                        </button>
                    </div>
                </div>
            )}

            {/* ===== LEFT PANEL — Branding / Content ===== */}
            <div
                className="flex w-full lg:w-1/2 relative overflow-hidden flex-col items-center justify-center p-8 lg:p-12 py-16 lg:py-0"
                style={{ background: BRAND.panelGradient }}
            >
                {/* Decorative blobs — colours driven by brand accent */}
                <div
                    className="absolute top-0 left-0 w-full h-full opacity-10"
                    style={{
                        backgroundImage: [
                            `radial-gradient(circle at 20% 50%, ${BRAND.decorativeGlow} 0%, transparent 50%)`,
                            `radial-gradient(circle at 80% 20%, rgba(23, 43, 77, 0.25) 0%, transparent 40%)`,
                            `radial-gradient(circle at 60% 80%, ${BRAND.decorativeGlow.replace('0.35', '0.2')} 0%, transparent 45%)`,
                        ].join(', '),
                    }}
                />
                <div
                    className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full opacity-5"
                    style={{ background: `radial-gradient(circle, ${BRAND.accentColorLight}, transparent)` }}
                />
                <div
                    className="absolute -top-16 -right-16 w-64 h-64 rounded-full opacity-5"
                    style={{ background: `radial-gradient(circle, ${BRAND.accentColorLight}, transparent)` }}
                />

                {/* Content */}
                <div className="relative z-10 max-w-md text-center">
                    <img
                        src={BRAND.logoSrc}
                        alt={BRAND.logoAlt}
                        className="h-16 w-auto mx-auto mb-8"
                    />

                    <h2 className="text-3xl font-bold text-white mb-4 leading-tight">
                        {BRAND.headline[0]}
                        <br />
                        <span style={{ color: BRAND.accentColorLight }}>{BRAND.headline[1]}</span>
                    </h2>

                    <p className="text-gray-300 text-sm leading-relaxed mb-10">
                        {BRAND.subhead}
                    </p>

                    {/* Feature highlights */}
                    <div className="space-y-4 text-left">
                        {BRAND.features.map((feat, i) => (
                            <div
                                key={i}
                                className="flex items-start gap-3 p-3 rounded-lg transition-all hover:bg-white/5"
                                style={{ backdropFilter: 'blur(4px)' }}
                            >
                                <span className="text-xl mt-0.5">{feat.icon}</span>
                                <div>
                                    <h4 className="text-white font-semibold text-sm">{feat.title}</h4>
                                    <p className="text-gray-400 text-xs leading-relaxed">{feat.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Bottom copyright */}
                <div className="lg:absolute lg:bottom-6 lg:left-0 lg:right-0 text-center mt-12 lg:mt-0">
                    <p className="text-white/60 lg:text-gray-500 text-xs">
                        © {new Date().getFullYear()} {BRAND.copyright}. All rights reserved.
                    </p>
                </div>
            </div>

            {/* ===== RIGHT PANEL — Login Form ===== */}
            <div className="w-full lg:w-1/2 flex-1 flex items-center justify-center bg-gray-50 p-6 sm:p-8 py-12 lg:py-8">
                <div className="max-w-md w-full">
                    {/* Login Card */}
                    <div className="bg-white shadow-xl rounded-2xl p-8 border border-gray-100">
                        <div className="text-center mb-8">
                            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                                Welcome back
                            </h1>
                            <p className="mt-2 text-sm text-gray-500">
                                {BRAND.signInSubtext}
                            </p>
                        </div>

                        <form
                            onSubmit={e => {
                                e.preventDefault();
                                if (authMethod === 'password') {
                                    handlePasswordLogin(e);
                                } else {
                                    handleMagicLinkRequest(e);
                                }
                            }}
                            className="space-y-5"
                        >

                    {/* Email */}
                    <FormInput
                        id="email"
                        label="Email address"
                        name="email"
                        autoComplete="username"
                        defaultValue={email}
                        onChange={e => setEmail(e.target.value)}
                        type="email"
                        required
                        className="w-full"
                    />

                            {/* Auth Method */}
                            <div>
                                <label className="block text-sm font-medium mb-2 text-gray-700">
                                    Sign in with
                                </label>
                                <div className="flex bg-gray-100 p-1 rounded-lg">
                                    <button
                                        type="button"
                                        onClick={() => setAuthMethod('password')}
                                        className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all ${authMethod === 'password'
                                            ? 'bg-white text-primary shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700'
                                            }`}
                                    >
                                        Password
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setAuthMethod('magic')}
                                        className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all ${authMethod === 'magic'
                                            ? 'bg-white text-primary shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700'
                                            }`}
                                    >
                                        Email link
                                    </button>
                                </div>
                            </div>

                    {/* Auth Actions */}
                    {authMethod === 'password' ? (
                        <div className="space-y-4">
                            <PasswordInput
                                id="password"
                                label="Password"
                                name="password"
                                autoComplete="current-password"
                                defaultValue={password}
                                onChange={e => setPassword(e.target.value)}
                                required
                                className="w-full"
                            />

                            <button
                                type="submit"
                                disabled={isLoading}
                                        className="w-full h-11 px-4 py-3 text-sm font-bold text-white disabled:opacity-70 disabled:cursor-not-allowed rounded-lg shadow-lg transition-all flex items-center justify-center"
                                        style={{ background: isLoading ? BRAND.accentColor : BRAND.buttonGradient }}
                                    >
                                        {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Sign in'}
                                    </button>
                                </div>
                            ) : (
                                <div>
                                    <p className="text-sm text-gray-500 mb-4 bg-primary/5 p-3 rounded-lg border border-primary/10">
                                        We'll send a secure, one-time login link to <strong>{email || 'your email'}</strong>.
                                        No password required.
                                    </p>

                                    <button
                                        type="submit"
                                        onClick={handleMagicLinkRequest}
                                        disabled={isLoading || !email || !EMAIL_REGEX.test(email)}
                                        className="w-full h-11 px-4 py-3 text-sm font-bold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-70 disabled:cursor-not-allowed rounded-lg shadow-sm transition-all flex items-center justify-center gap-2"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                        </svg>
                                        {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Send login link'}
                                    </button>
                                </div>
                            )}
                        </form>

                        <div className="h-[24px] mt-5">
                            {message && (
                                <p className="text-center text-sm font-medium text-red-600 bg-red-50 p-2 rounded">
                                    {message}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Footer beneath card */}
                    <p className="text-center text-xs text-gray-400 mt-6">
                        <a
                            href={BRAND.poweredByUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-gray-600 transition-colors"
                        >
                            Powered by{' '}
                            <span className="font-semibold text-gray-500">{BRAND.poweredByLabel}</span>
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );

};

LoginPage.propTypes = {
    onLogin: PropTypes.func.isRequired,
    onRequestMagicLink: PropTypes.func.isRequired,
    onMagicLogin: PropTypes.func.isRequired,
    loginWithToken: PropTypes.func.isRequired,
};

export const AuthGate = () => {
    const { user, token, login, requestMagicLink, loginWithMagicToken, loginWithToken, loading, logout, refreshUser } = useAuth();
    const location = useLocation();

    // Check for Magic Link Token in URL
    const params = new URLSearchParams(location.search);
    const magicToken = params.get('token');

    if (loading) return <div className="h-screen w-screen flex items-center justify-center"><LoadingSpinner size={40} /></div>;

    // IF Magic Token is present, PRIORITIZE LoginPage to handle it (even if user is logged in)
    // This allows switching users via magic link.
    if (magicToken || !token || !user) {
        if (!magicToken && (token || user)) {
            console.debug("[AuthGate] Transient state detected - showing LoginPage?", { hasToken: !!token, hasUser: !!user });
        }
        return <LoginPage onLogin={login} onRequestMagicLink={requestMagicLink} onMagicLogin={loginWithMagicToken} loginWithToken={loginWithToken} />;
    }

    // Role-Based Layout Redirect
    // All roles now use PlatformLayout (Unified Portal)
    return (
        <PlatformLayout onLogout={logout} token={token}>
            <Routes>
                <Route path="/" element={<Navigate to="/home" replace />} />
                <Route path="/home" element={<AdminDashboardPage />} />
                <Route path="/partners" element={<PartnersPage />} />
                <Route path="/partners/:id" element={<PartnerDetailPage />} />
                <Route path="/tenants" element={<TenantsPage />} />
                <Route path="/tenants/:id" element={<TenantDetailPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/roles" element={<RolesPermissionsPage token={token} currentUser={user} refreshUser={refreshUser} />} />
                <Route path="/apps" element={<BusinessAppsPage />} />
                <Route path="/datamodels" element={<DataModelsPage />} />
                <Route path="/datasets" element={<DatasetsPage />} />
                <Route path="/datasets" element={<DatasetsPage />} />
                <Route path="/dashboards" element={<DashboardsLandingPage />} />
                <Route path="/dashboards/create" element={<DashboardsPage />} />
                <Route path="/dashboards/:id/view" element={<DashboardViewPage />} />
                <Route path="/dashboards/:id/edit" element={<DashboardsPage />} />
                <Route path="/platform/app-runner/:contentId" element={<MLAppRunnerPage />} />
                <Route path="/dashboards/:id" element={<DashboardViewPage />} />
                <Route path="/alert-rules" element={<AlertRulesPage token={token} currentUser={user} />} />
                <Route path="/credits" element={<CreditsPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                {/* DQ Studio Routes */}
                <Route path="/dq/profiler" element={<DqProfilerPage />} />
            </Routes>
        </PlatformLayout>
    );
};

export default AuthGate;
